import { CommonModule } from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DomSanitizer } from '@angular/platform-browser';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  distinctUntilChanged,
} from 'rxjs';
import { FilesService } from '../../services/file.service';
import { ItemFileComponent, SizeFileComponent } from './file-item.component';
import { Environment, jsonToFormData } from '@t-llevo/util';

const THUMBUP_ICON = `
  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" >
  <path d="M0 0h24v24H0V0z" fill="none"/>
  <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
  </svg>
`;

const DELETE_ICON = `
<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="m376-300 104-104 104 104 56-56-104-104 104-104-56-56-104 104-104-104-56 56 104 104-104 104 56 56Zm-96 180q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520Zm-400 0v520-520Z"/></svg>
`;

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'edt-upload-file',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    ItemFileComponent,
    SizeFileComponent,
  ],
  template: `
    <input
      type="file"
      (change)="onFileSelected($event)"
      [accept]="acceptTypes.join()"
      style="display: none;"
      #fileUpload
    />
    @if (!((upload$ | async) ?? false)) {
    <div class="file-upload">
      {{ initialMessage }}

      <button mat-icon-button (click)="fileUpload.click()">
        <mat-icon svgIcon="attach_file" class="d-flex"></mat-icon>
      </button>
    </div>
    } @if (((upload$ | async) ?? false) && file) { @if (type === 'expand') {

    <div class="card-upload">
      <div class="header-upload">
        <edt-file-item [name]="file.name" [type]="file.type"></edt-file-item>
        <button mat-icon-button (click)="_cancelUpload()">
          <mat-icon
            class="cancel-upload d-flex"
            svgIcon="delete_forever"
          ></mat-icon>
        </button>
      </div>
      <edt-size-file [bytes]="file.size"></edt-size-file>
      <div class="progress-upload">
        <mat-progress-bar
          class="me-2"
          mode="determinate"
          [value]="uploadProgress$ | async"
        >
        </mat-progress-bar>

        {{ uploadProgress$ | async }}%
      </div>
    </div>

    } @else {

    <div class="mini-upload">
      <edt-file-item
        [name]="file.name"
        [type]="file.type"
        [onlyIcon]="true"
      ></edt-file-item>
      <div class="progress-upload-mini">
        <mat-progress-bar mode="determinate" [value]="uploadProgress$ | async">
        </mat-progress-bar>
      </div>
    </div>

    }
    <ng-template #mini>
      <div class="mini-upload">
        <edt-file-item
          [name]="file.name"
          [type]="file.type"
          [onlyIcon]="true"
        ></edt-file-item>
        <div class="progress-upload-mini">
          <mat-progress-bar
            mode="determinate"
            [value]="uploadProgress$ | async"
          >
          </mat-progress-bar>
        </div>
      </div>
    </ng-template>

    }
  `,
  styles: [
    '.card-upload{flex-direction:row;border:0.1rem solid #dee2e6;padding: 0.5rem}',
    '.header-upload{display:flex; align-items:center;justify-content: space-between;}',
    '.progress-upload{display:flex; align-items:center;}',
    '.progress-upload-mini{margin-top:0.3rem;}',
    '.mini-upload{flex-direction:upload; align-items:center;}',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * The `UploadFileComponent` class is responsible for handling file uploads in an Angular application.
 * It provides methods for selecting a file, uploading the file to a server, and canceling the upload process.
 * It also emits events to notify the parent component of the upload status.
 *
 * Example Usage:
 * ```javascript
 * const component = new UploadFileComponent();
 * component.url = 'upload';
 * component.showProgressBar = true;
 * component.initialMessage = 'Attach document';
 * component.method = 'post';
 * component.acceptTypes = ['image/png', 'image/jpeg'];
 * component.finalizeUpload.subscribe((value) => {
 *   console.log(value); // true
 * });
 * component.errorUpload.subscribe((value) => {
 *   console.log(value); // true
 * });
 * component.cancelUpload.subscribe((value) => {
 *   console.log(value); // true
 * });
 *
 * component.onFileSelected({ target: { files: [new File([], 'test.png')] } });
 * component._cancelUpload();
 * ```
 *
 * Methods:
 * - `onFileSelected(event: any)`: Triggered when a file is selected by the user. Checks if a valid file is selected and initiates the file upload process. Emits events during the file upload process.
 * - `_cancelUpload()`: Cancels the file upload process. Unsubscribes from the upload subscription, resets the upload state variables, emits the `cancelUpload` event, and updates the upload subject to indicate that the upload has been canceled.
 * - `_reset()`: Resets the state of the `UploadFileComponent` class when an upload is canceled or completed. Resets the file, upload subscription, upload subject, and upload progress variables.
 * - `_uploadFile(url: string, method: 'post' | 'patch', formData: FormData, options: any): Observable<any>`: Sends an HTTP request to upload the file to the server using the specified URL, method, form data, and options. Returns an observable of the response.
 * - `validFileType(file: File): boolean`: Checks if the file type is valid based on the accepted file types specified in the component's input.
 *
 * Fields:
 * - `url: string | null`: The URL to which the file will be uploaded.
 * - `showProgressBar: boolean`: Determines whether to show the progress bar during the file upload.
 * - `initialMessage: string`: The initial message displayed when no file is selected.
 * - `method: 'post' | 'patch'`: The HTTP method to use for the file upload.
 * - `type: 'mini' | 'expand'`: Show compress UI or not.
 * - `acceptTypes: string[]`: The accepted file types for the file input.
 * - `finalizeUpload: EventEmitter<boolean>`: An event emitter that emits a boolean value when the file upload is completed.
 * - `errorUpload: EventEmitter<boolean>`: An event emitter that emits a boolean value when an error occurs during the file upload.
 * - `cancelUpload: EventEmitter<boolean>`: An event emitter that emits a boolean value when the file upload is canceled.
 * - `file: File | null`: The selected file.
 * - `uploadSub: Subscription | null`: The subscription to the file upload observable.
 * - `uploadSubject: BehaviorSubject<boolean>`: A subject that indicates the state of the file upload.
 * - `uploadProgress: BehaviorSubject<number>`: A subject that tracks the progress of the file upload.
 *
 * @example
 * const component = new UploadFileComponent();
 * component.url = 'upload';
 * component.showProgressBar = true;
 * component.initialMessage = 'Attach document';
 * component.method = 'post';
 * component.acceptTypes = ['image/png', 'image/jpeg'];
 * component.finalizeUpload.subscribe((value) => {
 *   console.log(value); // true
 * });
 * component.errorUpload.subscribe((value) => {
 *   console.log(value); // true
 * });
 * component.cancelUpload.subscribe((value) => {
 *   console.log(value); // true
 * });
 *
 * component.onFileSelected({ target: { files: [new File([], 'test.png')] } });
 * component._cancelUpload();
 */
export class UploadFileComponent implements OnDestroy {
  @Input() url: string | null = null;
  @Input() showProgressBar = true;
  @Input() initialMessage = 'Adjuntar documento';
  @Input() method: 'post' | 'patch' = 'post';
  @Input() needName = true;
  @Input() type: 'mini' | 'expand' = 'expand';
  @Input() acceptTypes = [
    'image/apng',
    'image/bmp',
    'image/gif',
    'image/jpeg',
    'image/pjpeg',
    'image/png',
    'image/svg+xml',
    'image/tiff',
    'image/webp',
    'image/x-icon',
    'application/pdf',
    '.doc',
    '.docx',
    '.xml',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  @Output() finalizeUpload = new EventEmitter<boolean>();
  @Output() errorUpload = new EventEmitter<boolean>();
  @Output() cancelUpload = new EventEmitter<boolean>();
  file: File | null = null;
  uploadSub: Subscription | null = null;
  uploadSubject = new BehaviorSubject<boolean>(false);
  uploadProgress = new BehaviorSubject<number>(0);
  uploadProgress$ = this.uploadProgress
    .asObservable()
    .pipe(distinctUntilChanged());
  upload$ = this.uploadSubject.asObservable().pipe(distinctUntilChanged());

  constructor(
    private fileSrv: FilesService,
    private http: HttpClient,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    @Inject(Environment) private env: Environment
  ) {
    iconRegistry.addSvgIconLiteral(
      'attach_file',
      sanitizer.bypassSecurityTrustHtml(THUMBUP_ICON)
    );

    iconRegistry.addSvgIconLiteral(
      'delete_forever',
      sanitizer.bypassSecurityTrustHtml(DELETE_ICON)
    );
  }
  /**
   * Triggered when a file is selected by the user. Checks if a valid file is selected and initiates the file upload process.
   * @param event The event object that contains the selected file.
   * @returns  None. Performs actions based on the selected file and emits events during the file upload process.
   */
  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    this.file = target && target.files ? target?.files[0] : null;
    if (this.file && this.validFileType(this.file)) {
      this.uploadSubject.next(true);
      const data: Record<string, unknown> = {
        file: this.file,
      };

      if (this.needName) {
        data['name'] = this.file.name;
      }
      const formData = jsonToFormData(data);
      const uploadFile$ = this._uploadFile(
        this.url?.trim() ?? 'upload',
        this.method,
        formData,
        { reportProgress: true, observe: 'events' }
      );

      this.uploadSub = uploadFile$.subscribe({
        next: (event) => {
          if (event.type == HttpEventType.UploadProgress) {
            this.uploadProgress.next(
              Math.round(100 * (event.loaded / event.total))
            );
          }
        },
        error: () => {
          this.errorUpload.emit(true);
        },
        complete: () => {
          this._reset();
          this.finalizeUpload.emit(true);
        },
      });
    } else {
      this.errorUpload.emit(true);
    }
  }

  /**
   * Cancel request, un subscribe
   """
   Cancels the file upload process.

   Unsubscribes from the upload subscription, resets the upload state variables, emits the `cancelUpload` event, and updates the upload subject to indicate that the upload has been canceled.

   Example Usage:
   ```typescript
   const component = new UploadFileComponent();
   component.uploadSub = new Subscription();
   component.uploadSubject = new BehaviorSubject<boolean>(true);
   component.cancelUpload.subscribe((value) => {
     console.log(value); // true
   });

   component._cancelUpload();
   ```

   Inputs:
   None

   Flow:
   1. Check if the `uploadSub` variable is truthy (i.e., a subscription exists).
   2. If `uploadSub` is truthy, unsubscribe from the subscription using the `unsubscribe()` method.
   3. Reset the upload state variables by calling the `_reset()` method.
   4. Update the upload subject to indicate that the upload has been canceled by setting its value to `false`.
   5. Emit the `cancelUpload` event with the value `true`.

   Outputs:
   None
   """

      */
  _cancelUpload() {
    if (this.uploadSub) {
      this.uploadSub.unsubscribe();
      this._reset();
      this.uploadSubject.next(false);
      this.cancelUpload.emit(true);
    }
  }

  /**
   * Resets the state of the UploadFileComponent class when an upload is canceled or completed.
   *
   * Example Usage:
   * ```typescript
   * const uploadComponent = new UploadFileComponent();
   * uploadComponent._reset();
   * ```
   *
   * Inputs: None
   *
   * Flow:
   * 1. Check if `uploadSub` is not null.
   * 2. If `uploadSub` is not null, reset the `file` property to null.
   * 3. Reset the `uploadSub` property to null.
   * 4. Set the `uploadSubject` to false.
   * 5. Set the `uploadProgress` to 0.
   *
   * Outputs: None
   */
  private _reset(): void {
    if (this.uploadSub) {
      this.file = null;
      this.uploadSub = null;
      this.uploadSubject.next(false);
      this.uploadProgress.next(0);
    }
  }

  ngOnDestroy(): void {
    if (this.uploadSub) {
      this.uploadSub = null;
    }
  }

  /**
   *
   * @param url The HTTP method.
   * @param method The endpoint URL.
   * @param formData
   * @param options The HTTP options to send with the request.
   * @returns An `Observable` of the response, with the response body of type any.
   */
  private _uploadFile(
    url: string,
    method: 'post' | 'patch',
    formData: FormData,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    if (url.startsWith('/')) {
      url = url.substring(1);
    }

    if (url.endsWith('/')) {
      url = url.substring(0, url.length - 1);
    }

    return this.http[method](`${this.env.apiUrl}/${url}/`, formData, options);
  }

  /**
   * Check if the file type of the given file is included in the acceptTypes array.
   * @param file (File): The file to check its type.
   * @returns bool: True if the file type is included in the acceptTypes array, False otherwise.
   */

  validFileType(file: File) {
    return this.acceptTypes.includes(file.type);
  }
}
